// Custom folders: user-made, nestable folders inside a project that hold *references* to shots, canvases,
// elements and assets. An item can sit in several folders; adding or moving never copies the item.
//
// Everything the UI does with folders goes through the `FolderStore` interface: cached synchronous reads and
// async writes. Today it is backed by localStorage (`createLocalFolderStore`); the folder endpoints the backend
// is adding get an API-backed store with the same interface, and swapping it in App.tsx is the only change.
// Folders are never deleted, only archived (the datamodel rule of the hub).

export type FolderItemKind = "shot" | "canvas" | "element" | "asset" | "scene" | "episode";

export interface FolderItemRef {
  kind: FolderItemKind;
  id: string;
}

export interface FolderEntry extends FolderItemRef {
  addedAt: string;
}

export interface Folder {
  id: string;
  projectId: string;
  parentId: string | null;
  name: string;
  /** Order among the folders with the same parent (lowest first). */
  position: number;
  createdAt: string;
  archivedAt: string | null;
  items: FolderEntry[];
}

/** Where a project’s folders stand: an API-backed store fetches them, the local one is always ready. */
export type FolderLoad = { status: "idle" | "loading" | "ready" } | { status: "error"; error: string };

/**
 * Everything the UI knows about folders. Reads are synchronous and come from a cache (`load` fills it),
 * writes are async and update the cache before they resolve, so a localStorage store and an API store are
 * interchangeable: swapping them is a change in one file (the store given to AppContext in App.tsx).
 * Snapshots are stable: `list` returns the same array until something in that project changed.
 */
export interface FolderStore {
  /** Cached live folders of a project (archived ones left out), in creation order. */
  list(projectId: string): Folder[];
  /**
   * Archived folders of a project that can be brought back on their own: those whose parent is live or at
   * the top level (a subfolder archived together with its parent comes back with that parent). Newest archive
   * first. Snapshots are stable like `list`.
   */
  archived(projectId: string): Folder[];
  get(id: string): Folder | undefined;
  state(projectId: string): FolderLoad;
  /** Fetch (or refetch) a project’s folders into the cache. Never rejects: failures show up in `state`. */
  load(projectId: string): Promise<void>;
  create(projectId: string, name: string, parentId?: string | null): Promise<Folder>;
  rename(id: string, name: string): Promise<Folder>;
  /** Re-parent a folder. Refuses to move a folder into itself or one of its descendants. */
  move(id: string, parentId: string | null): Promise<Folder>;
  /** Put these sibling folders (all with this parent) in this order. */
  reorder(projectId: string, parentId: string | null, ids: string[]): Promise<void>;
  /** Archive a folder and its subfolders. The items they referenced are untouched. */
  archive(id: string): Promise<void>;
  /** Undo an archive: the folder comes back with the subfolders archived together with it. */
  restore(id: string): Promise<Folder>;
  /** Add a reference. Adding the same item twice is a no-op. */
  addItem(id: string, ref: FolderItemRef): Promise<Folder>;
  removeItem(id: string, ref: FolderItemRef): Promise<Folder>;
  subscribe(fn: () => void): () => void;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const FOLDER_STORAGE_KEY = "filmsquare.folders.v1";
export const MAX_NAME = 120;

export class FolderError extends Error {
  code: "not_found" | "invalid_name" | "cycle" | "forbidden" | "failed";
  constructor(code: FolderError["code"], message: string) {
    super(message);
    this.name = "FolderError";
    this.code = code;
  }
}

// ─── pure helpers ────────────────────────────────────────────────────────────

export function cleanName(name: string): string {
  const n = name.replace(/\s+/g, " ").trim().slice(0, MAX_NAME);
  if (!n) throw new FolderError("invalid_name", "A folder needs a name");
  return n;
}

export const sameRef = (a: FolderItemRef, b: FolderItemRef): boolean => a.kind === b.kind && a.id === b.id;

/** Folders in the order the sidebar shows them: position, then name (case-insensitive), then id. */
export function sortFolders(list: readonly Folder[]): Folder[] {
  return [...list].sort(
    (a, b) =>
      a.position - b.position ||
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** The live subfolders of a parent (null = top level), in display order. */
export function childrenOf(folders: readonly Folder[], parentId: string | null): Folder[] {
  return sortFolders(folders.filter((f) => f.parentId === parentId && !f.archivedAt));
}

/**
 * From all archived folders of a project: the ones to offer for restoring, newest archive first. A folder whose
 * parent is archived too is left out: restoring the parent brings it back (or, archived earlier on its own, it
 * shows up here once the parent is live again).
 */
export function restorable(archived: readonly Folder[]): Folder[] {
  const ids = new Set(archived.map((f) => f.id));
  return archived
    .filter((f) => !f.parentId || !ids.has(f.parentId))
    .sort(
      (a, b) =>
        (b.archivedAt ?? "").localeCompare(a.archivedAt ?? "") ||
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
}

/** Root → folder chain. Stops on a broken or cyclic parent link. */
export function pathOf(folders: readonly Folder[], id: string): Folder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const out: Folder[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return out;
}

export function descendantsOf(folders: readonly Folder[], id: string): Set<string> {
  const out = new Set<string>();
  const walk = (pid: string) => {
    for (const f of folders)
      if (f.parentId === pid && !out.has(f.id)) {
        out.add(f.id);
        walk(f.id);
      }
  };
  walk(id);
  return out;
}

/** Folders that reference an item. */
export function foldersContaining(folders: readonly Folder[], ref: FolderItemRef): Folder[] {
  return folders.filter((f) => !f.archivedAt && f.items.some((i) => sameRef(i, ref)));
}

/** Take an item out of every live folder that points at it; the item itself stays. */
export async function removeFromFolders(
  store: Pick<FolderStore, "removeItem">,
  folders: readonly Folder[],
  ref: FolderItemRef,
): Promise<void> {
  for (const f of foldersContaining(folders, ref)) await store.removeItem(f.id, ref);
}

/** "New folder", "New folder 2", … unique among siblings. */
export function nextName(siblings: readonly Folder[], base = "New folder"): string {
  const taken = new Set(siblings.map((f) => f.name.toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let i = 2; ; i++) if (!taken.has(`${base} ${i}`.toLowerCase())) return `${base} ${i}`;
}

// ─── localStorage store ──────────────────────────────────────────────────────

interface Saved {
  folders: Folder[];
}

function parse(raw: string | null): Folder[] {
  if (!raw) return [];
  try {
    const d = JSON.parse(raw) as Saved;
    if (!Array.isArray(d?.folders)) return [];
    // Folders saved before positions existed keep their saved order: position = place in the array.
    return d.folders
      .filter((f) => f && typeof f.id === "string")
      .map((f, i) => (typeof f.position === "number" ? f : { ...f, position: i }));
  } catch {
    return [];
  }
}

export interface LocalStoreOptions {
  storage?: StorageLike | null;
  key?: string;
  now?: () => Date;
  newId?: () => string;
}

/** A FolderStore kept in localStorage (or in memory when storage is unavailable). */
export function createLocalFolderStore(opts: LocalStoreOptions = {}): FolderStore {
  const key = opts.key ?? FOLDER_STORAGE_KEY;
  const storage = opts.storage === undefined ? defaultStorage() : opts.storage;
  const now = opts.now ?? (() => new Date());
  const newId = opts.newId ?? (() => crypto.randomUUID());
  let folders: Folder[] = parse(safeRead(storage, key));
  const subs = new Set<() => void>();

  const save = () => {
    snaps.clear();
    asnaps.clear();
    if (storage) {
      try {
        storage.setItem(key, JSON.stringify({ folders } satisfies Saved));
      } catch {
        // storage full or blocked: keep working in memory
      }
    }
    for (const fn of subs) fn();
  };
  const need = (id: string): Folder => {
    const f = folders.find((x) => x.id === id && !x.archivedAt);
    if (!f) throw new FolderError("not_found", "That folder no longer exists");
    return f;
  };
  const replace = (next: Folder): Folder => {
    folders = folders.map((f) => (f.id === next.id ? next : f));
    save();
    return next;
  };

  /** The position after the last live sibling in a parent. */
  const endOf = (projectId: string, parentId: string | null, exceptId?: string): number =>
    Math.max(
      -1,
      ...folders
        .filter(
          (f) => f.projectId === projectId && f.parentId === parentId && !f.archivedAt && f.id !== exceptId,
        )
        .map((f) => f.position),
    ) + 1;

  const READY: FolderLoad = { status: "ready" };
  const snaps = new Map<string, Folder[]>();
  const list = (projectId: string): Folder[] => {
    let l = snaps.get(projectId);
    if (!l) {
      l = folders.filter((f) => f.projectId === projectId && !f.archivedAt);
      snaps.set(projectId, l);
    }
    return l;
  };

  const asnaps = new Map<string, Folder[]>();
  const archived = (projectId: string): Folder[] => {
    let l = asnaps.get(projectId);
    if (!l) {
      l = restorable(folders.filter((f) => f.projectId === projectId && f.archivedAt));
      asnaps.set(projectId, l);
    }
    return l;
  };

  return {
    list,
    archived,
    get: (id) => folders.find((f) => f.id === id && !f.archivedAt),
    state: () => READY,
    load: async () => undefined,
    async create(projectId, name, parentId = null) {
      if (parentId) {
        const p = need(parentId);
        if (p.projectId !== projectId) throw new FolderError("not_found", "Parent is in another production");
      }
      const f: Folder = {
        id: newId(),
        projectId,
        parentId,
        name: cleanName(name),
        position: endOf(projectId, parentId),
        createdAt: now().toISOString(),
        archivedAt: null,
        items: [],
      };
      folders = [...folders, f];
      save();
      return f;
    },
    async rename(id, name) {
      return replace({ ...need(id), name: cleanName(name) });
    },
    async move(id, parentId) {
      const f = need(id);
      if (parentId) {
        const p = need(parentId);
        if (p.projectId !== f.projectId)
          throw new FolderError("not_found", "Parent is in another production");
        if (parentId === id || descendantsOf(folders, id).has(parentId))
          throw new FolderError("cycle", "A folder cannot go inside itself");
      }
      return replace({ ...f, parentId, position: endOf(f.projectId, parentId, id) });
    },
    async reorder(projectId, parentId, ids) {
      const at = new Map(ids.map((id, i) => [id, i]));
      for (const id of ids) {
        const f = need(id);
        if (f.projectId !== projectId || f.parentId !== parentId)
          throw new FolderError("not_found", "Those folders are not in the same place");
      }
      folders = folders.map((f) => (at.has(f.id) ? { ...f, position: at.get(f.id)! } : f));
      save();
    },
    async archive(id) {
      need(id);
      const gone = descendantsOf(folders, id).add(id);
      const at = now().toISOString();
      folders = folders.map((f) => (gone.has(f.id) && !f.archivedAt ? { ...f, archivedAt: at } : f));
      save();
    },
    async restore(id) {
      const f = folders.find((x) => x.id === id);
      if (!f) throw new FolderError("not_found", "That folder no longer exists");
      const at = f.archivedAt;
      if (!at) return f;
      folders = folders.map((x) => (x.archivedAt === at ? { ...x, archivedAt: null } : x));
      save();
      return folders.find((x) => x.id === id)!;
    },
    async addItem(id, ref) {
      const f = need(id);
      if (f.items.some((i) => sameRef(i, ref))) return f;
      return replace({
        ...f,
        items: [...f.items, { kind: ref.kind, id: ref.id, addedAt: now().toISOString() }],
      });
    },
    async removeItem(id, ref) {
      const f = need(id);
      return replace({ ...f, items: f.items.filter((i) => !sameRef(i, ref)) });
    },
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function safeRead(storage: StorageLike | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
